<template>
  <div class="session-content-list">
    <div v-for="item in items" :key="item.id" class="scl-item">
      <!-- Programme row (expandable to its drills) -->
      <template v-if="item.programme_id">
        <div class="scl-row is-programme" @click="toggleProgramme(item.programme_id)">
          <i class="bi bi-collection scl-row-icon"></i>
          <div class="scl-row-text">
            <span class="scl-row-title">{{ item.programme_title }}</span>
            <span v-if="item.programme_description" class="scl-row-sub">{{
              item.programme_description
            }}</span>
          </div>
          <i class="bi scl-row-chevron" :class="expandedProgrammes[item.programme_id]
            ? 'bi-chevron-up'
            : 'bi-chevron-down'
            "></i>
        </div>
        <div v-if="expandedProgrammes[item.programme_id]" class="scl-programme-drills">
          <div v-if="expandedProgrammes[item.programme_id].loading" class="scl-empty">
            Loading drills...
          </div>
          <template v-else>
            <div v-for="drill in expandedProgrammes[item.programme_id].drills" :key="drill.id" class="scl-row is-drill"
              @click="openDrill(drill.id)">
              <img v-if="drill.imageURL" :src="`/images/${drill.imageURL}`" class="scl-row-thumb" :alt="drill.name" />
              <i v-else class="bi bi-clipboard-check scl-row-icon"></i>
              <div class="scl-row-text">
                <span class="scl-row-title">{{ drill.name }}</span>
                <span class="scl-row-sub">{{ drillMeta(drill) }}</span>
              </div>
              <i class="bi bi-chevron-right scl-row-chevron"></i>
            </div>
            <p v-if="expandedProgrammes[item.programme_id].drills.length === 0" class="scl-empty">
              No drills in this programme yet.
            </p>
          </template>
        </div>
      </template>

      <!-- Single drill row -->
      <div v-else class="scl-row is-drill" @click="openDrill(item.drill_id)">
        <img v-if="item.imageURL" :src="`/images/${item.imageURL}`" class="scl-row-thumb" :alt="item.drill_name" />
        <i v-else class="bi bi-clipboard-check scl-row-icon"></i>
        <div class="scl-row-text">
          <span class="scl-row-title">{{ item.drill_name }}</span>
          <span class="scl-row-sub">{{ drillMeta(item) }}</span>
        </div>
        <i class="bi bi-chevron-right scl-row-chevron"></i>
      </div>
    </div>

    <!-- Full drill detail overlay -->
    <div v-if="viewingDrill" class="scl-drill-overlay" @click.self="closeDrill">
      <div class="scl-drill-dialog" @click.stop>
        <div class="scl-drill-header">
          <h3>{{ viewingDrill.name }}</h3>
          <button type="button" class="scl-close" @click="closeDrill">
            &times;
          </button>
        </div>
        <div class="scl-drill-body">
          <div v-if="loadingDrill" class="scl-empty">Loading drill...</div>
          <template v-else>
            <img v-if="viewingDrill.imageURL" :src="`/images/${viewingDrill.imageURL}`" class="scl-drill-image"
              :alt="viewingDrill.name" />

            <div class="scl-drill-tags">
              <span v-if="viewingDrill.category" class="scl-tag"><i class="bi bi-folder"></i>
                {{ viewingDrill.category }}</span>
              <span v-if="viewingDrill.sub_category" class="scl-tag"><i class="bi bi-folder2-open"></i>
                {{ viewingDrill.sub_category }}</span>
              <span v-if="viewingDrill.training_type" class="scl-tag"><i class="bi bi-lightning"></i>
                {{ viewingDrill.training_type }}</span>
              <span v-if="viewingDrill.age_level" class="scl-tag"><i class="bi bi-people"></i>
                {{ viewingDrill.age_level }}</span>
            </div>

            <p v-if="viewingDrill.introText" class="scl-drill-intro">
              {{ viewingDrill.introText }}
            </p>

            <div v-if="viewingDrill.details" class="scl-drill-details" v-html="viewingDrill.details"></div>

            <div v-if="viewingDrill.notes" class="scl-drill-notes">
              <h4><i class="bi bi-chat-right-text"></i> Coaching notes</h4>
              <p>{{ viewingDrill.notes }}</p>
            </div>

            <div v-if="drillBoards.length > 0" class="scl-drill-boards">
              <h4><i class="bi bi-easel2"></i> Boards</h4>
              <div v-for="board in drillBoards" :key="board.id" class="scl-board">
                <p class="scl-board-title">{{ board.title }}</p>
                <img v-if="board.thumbnail" :src="board.thumbnail" :alt="board.title" class="scl-board-thumb" />
                <div v-else class="scl-board-canvas">
                  <TacticalBoardCanvas :title="board.title || 'Board'" :savedElements="board.elements || []"
                    :background="board.background || 'full-pitch'" :savedBackgroundImage="board.background_image || ''"
                    :orientation="board.orientation || 'portrait'"
                    :imageTransform="board.image_transform || { x: 0, y: 0, scale: 1 }" :editMode="false" />
                </div>
                <p v-if="board.custom_notes" class="scl-board-notes">
                  {{ board.custom_notes }}
                </p>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TacticalBoardCanvas from "../TacticalBoard/TacticalBoardCanvas.vue";
import { TrainingPlan } from "@/services/training-plan-service.js";
import { TrainingProgramme } from "@/services/training-programme-service.js";
import { TacticalBoardService } from "@/services/tactical-board-service.js";

/**
 * Renders the training content (programmes + drills) attached to a team
 * training session, as returned by the games endpoint's getSessionDrills.
 *
 * Programmes expand inline to list their drills; tapping any drill opens a
 * full-detail overlay (image, intro, details, coaching notes, and linked
 * tactical boards). Used by the teams game-details modal and the player
 * TrainingWeek session modal.
 */
export default {
  name: "SessionContentList",
  components: { TacticalBoardCanvas },
  props: {
    // Rows from getSessionDrills: drill rows have drill_id/drill_name,
    // programme rows have programme_id/programme_title
    items: { type: Array, default: () => [] },
  },
  data() {
    return {
      expandedProgrammes: {},
      viewingDrill: null,
      loadingDrill: false,
      drillBoards: [],
    };
  },
  methods: {
    drillMeta(drill) {
      return [drill.category, drill.training_type].filter(Boolean).join(" · ");
    },
    async toggleProgramme(programmeId) {
      if (this.expandedProgrammes[programmeId]) {
        const collapsed = { ...this.expandedProgrammes };
        delete collapsed[programmeId];
        this.expandedProgrammes = collapsed;
        return;
      }
      this.expandedProgrammes = {
        ...this.expandedProgrammes,
        [programmeId]: { loading: true, drills: [] },
      };
      try {
        const responce = await TrainingProgramme.show(programmeId);
        this.expandedProgrammes = {
          ...this.expandedProgrammes,
          [programmeId]: {
            loading: false,
            drills: (responce.data && responce.data.drills) || [],
          },
        };
      } catch (error) {
        console.error("Error loading programme drills:", error);
        this.expandedProgrammes = {
          ...this.expandedProgrammes,
          [programmeId]: { loading: false, drills: [] },
        };
      }
    },
    async openDrill(drillId) {
      if (!drillId) return;
      this.viewingDrill = { name: "Drill" };
      this.loadingDrill = true;
      this.drillBoards = [];
      try {
        const [drillRes, boardsRes] = await Promise.all([
          TrainingPlan.find({ action: "getTrainingPlanById", id: drillId }),
          TacticalBoardService.getBoardsForTrainingPlan(drillId),
        ]);
        this.viewingDrill = drillRes.data || { name: "Drill" };
        this.drillBoards =
          boardsRes.data && boardsRes.data.success
            ? boardsRes.data.data || []
            : [];
      } catch (error) {
        console.error("Error loading drill details:", error);
      } finally {
        this.loadingDrill = false;
      }
    },
    closeDrill() {
      this.viewingDrill = null;
      this.drillBoards = [];
    },
  },
};
</script>

<style scoped lang="scss">
.scl-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 6px;
  cursor: pointer;

  &:hover {
    background: #eff6ff;
  }

  .scl-row-icon {
    font-size: 1.1rem;
    color: #3b82f6;
    flex-shrink: 0;
  }

  .scl-row-thumb {
    width: 42px;
    height: 42px;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
  }

  .scl-row-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
  }

  .scl-row-title {
    font-size: 0.88rem;
    font-weight: 600;
    color: #1e293b;
  }

  .scl-row-sub {
    font-size: 0.76rem;
    color: #64748b;
  }

  .scl-row-chevron {
    color: #94a3b8;
    flex-shrink: 0;
  }
}

.scl-programme-drills {
  margin: 0 0 6px 26px;

  .scl-row {
    background: #fdfdfd;
  }
}

.scl-empty {
  font-size: 0.84rem;
  color: #64748b;
  margin: 0;
  padding: 4px 0;
}

/* ---- Drill detail overlay ---- */
.scl-drill-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 1rem;
}

.scl-drill-dialog {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.scl-drill-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;

  h3 {
    margin: 0;
    font-size: 1.05rem;
    color: #1e293b;
  }

  .scl-close {
    background: none;
    border: none;
    font-size: 1.6rem;
    line-height: 1;
    cursor: pointer;
    color: #64748b;
  }
}

.scl-drill-body {
  padding: 1rem 1.25rem;
  overflow-y: auto;
}

.scl-drill-image {
  width: 100%;
  max-height: 320px;
  object-fit: contain;
  border-radius: 8px;
  background: #f8fafc;
  margin-bottom: 0.75rem;
}

.scl-drill-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 0.75rem;
}

.scl-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.74rem;
  font-weight: 500;
  color: #475569;
  background: #f1f5f9;
  padding: 3px 9px;
  border-radius: 12px;
}

.scl-drill-intro {
  font-size: 0.92rem;
  font-weight: 500;
  color: #334155;
  margin-bottom: 0.75rem;
}

.scl-drill-details {
  font-size: 0.88rem;
  color: #475569;
  margin-bottom: 0.75rem;

  :deep(img) {
    max-width: 100%;
  }
}

.scl-drill-notes {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 0.7rem 0.9rem;
  margin-bottom: 0.75rem;

  h4 {
    font-size: 0.82rem;
    font-weight: 700;
    color: #92400e;
    margin: 0 0 0.3rem;
  }

  p {
    font-size: 0.85rem;
    color: #78350f;
    margin: 0;
  }
}

.scl-drill-boards {
  h4 {
    font-size: 0.9rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 0.5rem;

    i {
      color: #3b82f6;
    }
  }
}

.scl-board {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.6rem;
  margin-bottom: 0.75rem;
}

.scl-board-title {
  font-size: 0.84rem;
  font-weight: 600;
  color: #334155;
  margin: 0 0 0.4rem;
}

.scl-board-thumb {
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  background: #fff;
}

.scl-board-canvas {
  :deep(.tactical-board-slide) {
    padding: 0;
    border: none;
    box-shadow: none;
    background: none;
  }

  :deep(.slide-header),
  :deep(.notes-panel),
  :deep(.notes-display) {
    display: none;
  }

  :deep(.drawing-canvas) {
    width: 100%;
    height: auto;
    max-height: none;
    box-shadow: none;
  }
}

.scl-board-notes {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0.4rem 0 0;
}
</style>
